import { Box, Stack, Tooltip } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useBundleTranslation from 'i18n';
import { selectAuthForNavigation, useAppDispatch, useAppSelector } from 'store/hooks';
import IconHandler from 'components/common/icon/IconHandler';
import styles from 'app/navbar/NavbarMenu.styles';
import getData, { mainMenuDataType, MainMenuItem, MainMenuItemsList, NavBarMenuComponentsRegistry } from './MenuConfig';
import IconMi from 'components/common/icon/IconMi';
import { useCheckUserAccess } from 'hooks/useCheckUserAccess';
import { customRequestAPI } from 'api/api';
import FormComponent from 'components/common/form/Form';

interface SubMenuProps {
    items?: MainMenuItemsList;
    active?: boolean;
    level?: number;
    onClickItem?: (item: MainMenuItem, event: React.MouseEvent<HTMLElement>) => void;
    openedMenu: any;
    subMenuActive: any;
    setSubMenuActive: any;
    rootItemKey?: string;
}

const MENU_WIDTH = 190;

function MenuBox({
    rootItem,
    setOpenedMenu,
    openedMenu,
    setNewModalData,
}: {
    rootItem: MainMenuItem;
    setOpenedMenu: any;
    openedMenu: string | null;
    setNewModalData: any;
}) {
    const [subMenuActive, setSubMenuActive] = React.useState<boolean>(false);
    const { t } = useBundleTranslation(['components/common/navbar/navbar_menu']);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const isActiveMenu = rootItem.key === openedMenu;

    const createNewFunc = (url: string, popupSettings?: any) => {
        setNewModalData('');

        if (popupSettings) {
            setNewModalData({ ...popupSettings, url });
        } else {
            customRequestAPI
                .post(url, {})
                .then((response: any) => {
                    if ('ERROR' === response.data.status) alert(response.data.message);
                    else if (response.data.redirectUrl) setTimeout(() => navigate(response.data.redirectUrl), 100);
                })
                .catch(() => {});
        }
    };

    const menuItemClick = (item: MainMenuItem, event: React.MouseEvent<HTMLElement>) => {
        if (!item.href && !item.action) return;

        event.stopPropagation();

        if (item.action) {
            item.action({ dispatch, createNewFunc });
        }

        setOpenedMenu(null);
    };

    const getUserItem = (username: string, isActive: boolean) => {
        const isNeedCut = username.length > 30;
        const tooltip = isNeedCut && !isActive ? username : '';
        const nick = isNeedCut ? username.slice(0, 30) + '...' : username;

        return (
            <Tooltip title={tooltip}>
                <Box component="span" className={'navbar-menu__root-item-label navbar-menu__root-item-label--username'}>
                    {nick}
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box
            key={rootItem.key}
            className={`navbar-menu__root-item-holder ${isActiveMenu ? 'navbar-menu__root-item-holder--active' : ''}`}
            onClick={(e) => {
                if (rootItem.children) {
                    setOpenedMenu(rootItem.key);
                } else {
                    menuItemClick(rootItem, e);
                }
            }}
            onMouseEnter={() => {
                if (openedMenu && !isActiveMenu && rootItem.children) setOpenedMenu(rootItem.key);
            }}
            onMouseLeave={() => {
                setSubMenuActive(false);
            }}
        >
            <Stack
                className={`navbar-menu__root-item`}
                direction="row"
                alignItems="center"
                component={!rootItem.href ? 'div' : rootItem.isExternal ? 'a' : Link}
                to={rootItem.href && !rootItem.isExternal ? rootItem.href : ''}
                href={rootItem.href && rootItem.isExternal ? rootItem.href : undefined}
            >
                <>
                    {rootItem.icon && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="center"
                            className={`navbar-menu__root-item-icon ${rootItem.key == 'user' && rootItem.icon.type == 'upload' ? 'avatar-icon' : ''}`}
                        >
                            <IconHandler icon={rootItem.icon} />
                        </Stack>
                    )}
                    {rootItem.key == 'user' ? (
                        getUserItem(rootItem.label, isActiveMenu)
                    ) : (
                        <Box component="span" className={'navbar-menu__root-item-label'}>
                            {false === rootItem.translate ? rootItem.label : t(rootItem.label)}
                        </Box>
                    )}
                </>
            </Stack>
            <SubMenu
                rootItemKey={rootItem.key}
                items={rootItem.children}
                active={isActiveMenu}
                onClickItem={menuItemClick}
                openedMenu={openedMenu}
                subMenuActive={subMenuActive}
                setSubMenuActive={setSubMenuActive}
            />
        </Box>
    );
}

function SubMenu(props: SubMenuProps) {
    const {
        items = [],
        active,
        level = 1,
        onClickItem = (item, event) => {},
        setSubMenuActive,
        openedMenu = null,
        subMenuActive = null,
        rootItemKey,
    } = props;

    const { t } = useBundleTranslation(['components/common/navbar/navbar_menu']);
    const [dropdownDirection, setDropdownDirection] = React.useState<'left' | 'right'>('right');
    const [hoveredItem, setHoveredItem] = React.useState<null | string>(null);

    useEffect(() => {
        setHoveredItem(null);
    }, [openedMenu]);

    useEffect(() => {
        let delayTimerId: any;
        if (!subMenuActive) {
            delayTimerId = setTimeout(function () {
                setHoveredItem(null);
            }, 400);
        }

        return () => clearTimeout(delayTimerId);
    }, [subMenuActive]);

    const onHoverItem = (item: MainMenuItem, event: React.MouseEvent<HTMLElement>, level: number, index: number) => {
        //we can replace with key, bun we need to set keys for all items
        setHoveredItem(`${level}_${index}`);

        setSubMenuActive(true);

        if (item.children) {
            const node = event.target as HTMLElement;
            const parentMenu = node.closest('.navbar-menu__submenu') ?? node;
            const indentToRightSide = 10;
            const rightCornerCoord =
                parentMenu.getBoundingClientRect().x +
                parentMenu.getBoundingClientRect().width +
                MENU_WIDTH +
                indentToRightSide;
            const direction = rightCornerCoord > window.innerWidth ? 'left' : 'right';
            setDropdownDirection(direction);
        }
    };

    if (!items.length) return null;

    const isHoveredIndex = hoveredItem ? Number(hoveredItem.split('_')[1]) : null;

    const scrollMenu = level > 1 && rootItemKey == 'new';
    return (
        <Stack
            className={`navbar-menu__submenu navbar-submenu navbar-submenu--level-${level} ${scrollMenu ? 'navbar-submenu--scroll' : ''}`}
            sx={{
                ...styles.submenuItemHolder,
                display: active ? 'block' : 'none',
                width: MENU_WIDTH,
            }}
        >
            {items.map((item, index) => {
                if (item.divider) {
                    return <Box className={'navbar-submenu__divider'} key={index} />;
                }

                return (
                    <Stack
                        key={item.label}
                        component={'div'}
                        className={`navbar-submenu__item-holder navbar-submenu__item-holder--direction-${dropdownDirection} ${
                            isHoveredIndex === index ? 'navbar-submenu__item-holder--hovered' : ''
                        }`}
                        style={{ display: 'block' }}
                        onClick={(e: any) => {
                            onClickItem(item, e);
                        }}
                        onMouseEnter={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                            onHoverItem(item, event, level, index);
                        }}
                    >
                        <Stack
                            data-test={item?.key ?? undefined}
                            component={!item.href ? 'div' : item.isExternal ? 'a' : Link}
                            to={item.href && !item.isExternal ? item.href : ''}
                            href={item.href && item.isExternal ? item.href : undefined}
                            target={item.newTab ? '_blank' : undefined}
                            rel="noopener noreferrer"
                            className={`navbar-submenu__item`}
                        >
                            {item.icon && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="center"
                                    className={'navbar-submenu__item-icon'}
                                >
                                    <IconHandler
                                        icon={item.icon}
                                        sx={{ fontSize: item.icon?.type === 'fa' ? 14 : 16 }}
                                    />
                                </Stack>
                            )}
                            <Box className="navbar-submenu__item-label" component="span">
                                {false === item.translate ? item.label : t(item.label)}
                            </Box>
                            {item.children && (
                                <Box className="navbar-submenu__item-children-ico">
                                    <IconMi icon={'chevron-right'} />
                                </Box>
                            )}
                        </Stack>
                        <SubMenu
                            rootItemKey={rootItemKey}
                            onClickItem={props.onClickItem}
                            items={item.children}
                            level={level + 1}
                            setSubMenuActive={setSubMenuActive}
                            openedMenu={openedMenu}
                            subMenuActive={subMenuActive}
                        />
                    </Stack>
                );
            })}
        </Stack>
    );
}

export default function NavbarMenu(props: { view?: 'horizontal' | 'vertical'; dataType: mainMenuDataType }) {
    const { view = 'horizontal', dataType = 'all' } = props;
    const { t } = useBundleTranslation(['components/common/navbar/navbar_menu']);
    const [openedMenu, setOpenedMenu] = React.useState<null | string>(null);
    const [newModalData, setNewModalData] = useState<any>();

    const state = useAppSelector(selectAuthForNavigation);

    const navigate = useNavigate();
    const checkCondition = useCheckUserAccess();
    const menu: MainMenuItemsList = getData(t, state, checkCondition, dataType);

    useEffect(() => {
        if (!openedMenu) {
            if (dataType == 'leftbar') document.body.classList.remove('navbar-menu--state-opened');
        } else {
            if (dataType == 'leftbar') document.body.classList.add('navbar-menu--state-opened');
        }
    }, [openedMenu]);
    return (
        <ClickAwayListener
            onClickAway={() => {
                setOpenedMenu(null);
            }}
        >
            <Stack
                direction={view == 'horizontal' ? 'row' : 'column'}
                spacing={view == 'horizontal' ? 0.5 : 0.25}
                sx={styles.navBarMenu}
                className={`navbar-menu navbar-menu--${view} navbar-menu--${openedMenu ? 'opened' : 'closed'}`}
            >
                {menu.map((rootItem, index) => {
                    //@ts-ignore
                    if (rootItem.component) {
                        let props = {};
                        //@ts-ignore
                        if (rootItem.component == 'MenuGlobalDocuments') {
                            props = {
                                onChangeState: (opened: any) => {
                                    if (opened) setOpenedMenu(null);
                                },
                            };
                        }
                        //@ts-ignore
                        return React.createElement(NavBarMenuComponentsRegistry[rootItem.component], {
                            ...props,
                            key: index,
                        });
                    }
                    if (rootItem.divider) {
                        return <Box className={'navbar-menu__item-divider'} key={index} />;
                    }
                    return (
                        <MenuBox
                            key={index}
                            rootItem={rootItem}
                            setOpenedMenu={setOpenedMenu}
                            openedMenu={openedMenu}
                            setNewModalData={setNewModalData}
                        />
                    );
                })}
                {newModalData && (
                    <FormComponent
                        isPopup
                        popupType={'new'}
                        pk={newModalData.url}
                        uid={newModalData.uid}
                        settingsUrl={newModalData.url}
                        onHide={() => {
                            setNewModalData('');
                        }}
                        popupSettings={{}}
                        afterSave={(response: any) => {
                            if (newModalData.redirectAfter) {
                                if (response.data && response.data.redirectUrl) {
                                    setTimeout(() => navigate(response.data.redirectUrl), 100);
                                }
                            }
                        }}
                    />
                )}
            </Stack>
        </ClickAwayListener>
    );
}
